import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AlertIcon } from 'src/assets/tailwind/alert-circle.svg';
import { ReactComponent as CheckCircleIcon } from 'src/assets/tailwind/check-circle.svg';
import { ReactComponent as ExpandIcon } from 'src/assets/tailwind/expand-icon.svg';
import { ReactComponent as XIcon } from 'src/assets/tailwind/x-close.svg';
import { Button } from 'src/components/ui/button';
import { Icon } from 'src/components/ui/icon';
import { Progress } from 'src/components/ui/progress';
import { Typography } from 'src/components/ui/typography';
import { useUploadWidget } from 'src/context/UploadWidgetContext';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export default function UploadFloatingWidget() {
  const { t } = useTranslation();
  const { progress, maximizeUpload, closeUpload } = useUploadWidget();
  const { totalProgress, successCount, failedCount, fileCount, droppedCount } =
    progress;

  const isUploading = fileCount > 0 && totalProgress < 100;
  const isDone = fileCount > 0 && totalProgress === 100;
  const isPartialFailure = isDone && (failedCount > 0 || droppedCount > 0);
  const isFullSuccess = isDone && failedCount === 0 && droppedCount === 0;

  const headerIcon = isFullSuccess
    ? { icon: CheckCircleIcon, colorClass: 'tw:text-icon-success' }
    : isPartialFailure
    ? { icon: AlertIcon, colorClass: 'tw:text-icon-warning' }
    : null;

  const headerLabel = isFullSuccess
    ? t(successCount === 1 ? T.uploadedFileSingular : T.uploadedFilePlural, {
        count: successCount
      })
    : isPartialFailure
    ? t(fileCount === 1 ? T.filesUploadedOfSingular : T.filesUploadedOfPlural, {
        successCount,
        fileCount
      })
    : t(fileCount === 1 ? T.uploadingFileSingular : T.uploadingFilePlural, {
        count: fileCount
      });

  return (
    <Draggable handle=".upload-widget-drag" bounds="body">
      <div className="tw:fixed tw:bottom-6 tw:right-6 tw:z-[1300] tw:select-none tw:w-[280px]">
        <div className="tw:bg-primary tw:border tw:border-border-secondary tw:rounded-bw-2xl-3xl tw:shadow-e1 tw:flex tw:flex-col tw:overflow-hidden">
          {/* Header */}
          <div className="upload-widget-drag tw:flex tw:items-center tw:justify-between tw:px-m tw:py-s tw:cursor-grab tw:active:cursor-grabbing">
            <div className="tw:flex tw:items-center tw:gap-s tw:min-w-0">
              {headerIcon && (
                <Icon
                  icon={headerIcon.icon}
                  size={16}
                  colorClass={headerIcon.colorClass}
                  className="tw:shrink-0"
                />
              )}
              <Typography
                variant="h3"
                colorClass="tw:text-text-primary"
                className="tw:truncate"
              >
                {headerLabel}
              </Typography>
            </div>
            <div className="tw:flex tw:items-center tw:gap-xs">
              <Button
                hierarchy="ghost"
                iconOnly
                leftIcon={ExpandIcon}
                size="small"
                className="tw:shrink-0"
                onClick={maximizeUpload}
              />
              {isFullSuccess && (
                <Button
                  hierarchy="ghost"
                  iconOnly
                  leftIcon={XIcon}
                  size="small"
                  className="tw:shrink-0"
                  onClick={closeUpload}
                />
              )}
            </div>
          </div>

          {/* Progress row — uploading only */}
          {isUploading && (
            <div className="tw:flex tw:items-center tw:gap-l tw:px-m tw:py-s">
              <div className="tw:flex-1 tw:min-w-0">
                <Progress value={totalProgress} />
              </div>
              <Typography
                variant="h6"
                colorClass="tw:text-text-primary"
                className="tw:shrink-0 tw:whitespace-nowrap"
              >
                {totalProgress}%
              </Typography>
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
}
